import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { MoreOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';

import { Button, DropdownMenu } from '../ui';
import { useT } from '../../i18n';

/** Nhóm của một hành động, quyết định thứ tự và chỗ đặt vạch phân cách. */
export type NhomThaoTac = 'xem' | 'sua' | 'pha-huy';

export interface MucThaoTac {
  /** Khóa duy nhất trong một menu. */
  khoa: string;
  /** Nhãn tiếng Việt có dấu, động từ trước: `Xem chi tiết`, `Xoá chứng từ`. */
  nhan: string;
  /** Icon của `@ant-design/icons` — dùng lại đúng icon ở chỗ cũ. */
  icon?: ReactNode;
  onChon: () => void;
  /**
   * `false` thì mục **không xuất hiện**. Quyền và trạng thái dòng đi qua đây,
   * không đi qua `vo_hieu`: ADR-UI-0005 chốt là ẩn, không hiện mờ.
   */
  hien?: boolean;
  /**
   * Hiện nhưng không bấm được — chỉ dùng khi người dùng **có** quyền mà trạng
   * thái dòng tạm thời chặn, và lý do cần nói ra (`moTa`).
   */
  voHieu?: boolean;
  /** Lý do vô hiệu, hoặc hậu quả của hành động. Hiện dưới nhãn. */
  moTa?: string;
  nhom?: NhomThaoTac;
}

export interface MenuThaoTacProps {
  muc: MucThaoTac[];
  /** Tên bản ghi, ghép vào `aria-label` để trình đọc màn hình phân biệt dòng. */
  tenBanGhi?: string;
}

const THU_TU_NHOM: NhomThaoTac[] = ['xem', 'sua', 'pha-huy'];

/**
 * Cột `Thao tác` của mọi bảng: một nút `⋮` mở menu chứa hành động của dòng
 * (ADR-UI-0005).
 *
 * Thay cho dãy nút chỉ-có-icon mà §10.5 của đặc tả mô tả. Lý do đổi: `ChungTuView`
 * có tới 6 nút trong cột 190px — mỗi nút còn ~30px, dưới ngưỡng vùng bấm 32–36px
 * của §13.7 — và số nút đổi theo trạng thái dòng nên cột nhảy giữa các dòng.
 *
 * Cột đi kèm: `width: 72`, `align: 'center'`.
 *
 * ❌ Dòng mà **mọi** trạng thái và mọi quyền đều chỉ cho ra một hành động thì
 * đừng dùng component này: giữ nút icon trần, một cú bấm hơn hai.
 */
export function MenuThaoTac({ muc, tenBanGhi }: MenuThaoTacProps) {
  const { t } = useT();
  const [moRong, setMoRong] = useState(false);

  const items = useMemo<MenuProps['items']>(() => {
    const hienThi = muc.filter((m) => m.hien !== false);

    // Gom theo nhóm rồi chèn divider giữa các nhóm **có mục**. Menu còn một
    // nhóm thì không có divider nào.
    return THU_TU_NHOM.flatMap<NonNullable<MenuProps['items']>[number]>((nhom, chiSo) => {
      const cuaNhom = hienThi.filter((m) => (m.nhom ?? 'sua') === nhom);
      if (cuaNhom.length === 0) return [];

      const daCoNhomTruoc = THU_TU_NHOM.slice(0, chiSo).some((truoc) =>
        hienThi.some((m) => (m.nhom ?? 'sua') === truoc),
      );

      return [
        ...(daCoNhomTruoc ? [{ type: 'divider' as const, key: `vach-${nhom}` }] : []),
        ...cuaNhom.map((m) => ({
          key: m.khoa,
          icon: m.icon,
          danger: nhom === 'pha-huy',
          disabled: m.voHieu,
          label: m.moTa ? (
            <span className="flex flex-col">
              <span>{m.nhan}</span>
              <span className="text-xs text-content-muted">{m.moTa}</span>
            </span>
          ) : (
            m.nhan
          ),
          onClick: m.onChon,
        })),
      ];
    });
  }, [muc]);

  // Không còn hành động nào qua được bộ lọc quyền/trạng thái: để ô trống chứ
  // đừng hiện nút mở ra một menu rỗng.
  if (!items || items.length === 0) return null;

  const nhanTroNang = tenBanGhi
    ? t('chung.thaoTacKhacVoi', { ten: tenBanGhi })
    : t('chung.thaoTacKhac');

  return (
    <DropdownMenu
      menu={{ items }}
      placement="bottomRight"
      open={moRong}
      onOpenChange={setMoRong}
    >
      <Button
        variant="ghost"
        size="sm"
        /*
         * `title` **và** `aria-label` cho nút chỉ-có-icon (§13.7). Không bọc
         * thêm `Tooltip`: antd `Dropdown` đã gắn handler lên con trực tiếp, nên
         * chèn một lớp nữa vào giữa làm mất handler mở menu.
         */
        title={t('chung.thaoTacKhac')}
        aria-label={nhanTroNang}
        aria-expanded={moRong}
        onClick={(e) => {
          // Dòng bảng ở nhiều màn tự mở khung chi tiết khi bấm. Không chặn thì
          // mở menu cũng mở luôn khung đó.
          e.stopPropagation();
        }}
      >
        <MoreOutlined />
      </Button>
    </DropdownMenu>
  );
}
